import { Button } from "@/components/ui/button";

import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog } from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useThaloWeb } from "@/contexts/ThaloWebContext";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";
import { MultiSelect } from "./ui/multi-select";
import { useSelectedAssets } from "@/contexts/SelectedAssetsContext";
import { useAllAssets } from "@/contexts/AllAssetsContext";
import { useOrganizationsMinimal } from "@/organization/api";
import { useLocations } from "@/location/api";

type PopoverTriggerProps = React.ComponentPropsWithoutRef<
  typeof PopoverTrigger
>;

interface LocationSwitcherProps extends PopoverTriggerProps {
  singleLevel?: "installation" | "location" | "organization";
  className?: string;
}

export function Dropdown({
  selectedItem,
  onSelect,
  options,
  placeholder,
  className,
  disabled,
}: any) {
  const [open, setOpen] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            // variant="outline"
            aria-expanded={open}
            aria-label="Select a location"
            role="combobox"
            variant={"outline"}
            size="sm"
            className={cn(
              `lg:min-w-[240px] justify-start bg-white text-left font-normal text-foreground ${!selectedItem && "text-muted-foreground"
              }`,
              className
            )}
            // className={cn("flex-1 justify-between", className)}
            disabled={disabled}
          >
            {selectedItem?.name || placeholder}
            <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" side="bottom" align="start">
          <Command>
            <CommandList>
              <CommandInput placeholder="Search..." />
              <CommandEmpty>No locations found.</CommandEmpty>
              {options?.map((option: any) => (
                <CommandItem
                  key={`${option.id}`}
                  onSelect={() => {
                    onSelect?.(option);
                    setOpen(false);
                  }}
                  className="text-sm"
                >
                  {option.name}
                  <Check
                    className={cn(
                      "ml-auto h-4 w-4",
                      selectedItem?.id === option.id
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </Dialog>
  );
}

const MultiSelectInstallations = ({
  disabled,
  className,
}: any) => {
  const { allAssets } = useAllAssets();
  const { setSelectedAssetIds, selectedAssets } = useSelectedAssets()

  const options = allAssets.map((i) => ({
    value: i.id as number,
    label: i.name as string,
    disabled: !i.sensor_kit
  }));

  if (options?.length === 0) return null;
  return (
    <>
      <MultiSelect
        className={cn("lg:w-[240px]", className)}
        placeholder="Select assets"
        options={options}
        selected={selectedAssets.map((asset) => ({ label: asset.name, value: asset.id }))}
        onChange={(selectedOptions) => setSelectedAssetIds(selectedOptions.map((item) => item.value))}
        customSelectedText={`${selectedAssets?.length} asset(s) selected`}
        disabled={disabled}
      />
    </>
  );
};


const LocationDropdown = ({ selectedOrganization, selectedLocation, setSelectedLocation }: {
  selectedOrganization: any;
  selectedLocation: any;
  setSelectedLocation: (location: any) => void;
}) => {
  const { data: { results: allLocations }} = useLocations({
    pageSize: 100,
    filters: { organization_id: selectedOrganization.id }
  });

  const locations = React.useMemo(() => {
    return allLocations.map((location) => ({
      id: location.id,
      name: location.name,
    }));
  }, [allLocations]);

  const handleLocationSelect = (option: any) => {
    const location = allLocations.find((l) => l.id === option.id);
    setSelectedLocation(location);
  };

  return (
    <Dropdown
      options={locations}
      placeholder="Select a building"
      selectedItem={{
        name: selectedLocation?.name,
        id: selectedLocation?.id,
      }}
      onSelect={handleLocationSelect}
    />
  );
};

const SingleLevelLocationDropdown = ({
                                       className,
                                       selectedOrganization,
                                       selectedLocation,
                                       setSelectedLocation
                                     }: {
  className?: string;
  selectedOrganization: any;
  selectedLocation: any;
  setSelectedLocation: (location: any) => void;
}) => {
  if (!selectedOrganization) {
    return (
      <Dropdown
        className={className}
        options={[]}
        placeholder="Select a building"
        selectedItem={{ name: undefined, id: undefined }}
        onSelect={() => {}}
        disabled
      />
    );
  }

  return (
    <LocationDropdown
      selectedOrganization={selectedOrganization}
      selectedLocation={selectedLocation}
      setSelectedLocation={setSelectedLocation}
    />
  );
};

export default function LocationSwitcher({
                                           singleLevel,
                                           className,
                                         }: LocationSwitcherProps) {
  const {
    selectedLocation,
    selectedOrganization,
    setSelectedOrganization,
    setSelectedLocation,
  } = useThaloWeb();

  const { data: { results: organizations }} = useOrganizationsMinimal();

  const handleOrganizationSelect = (option: any) => {
    const newOrganization = organizations.find((o) => o.id === option.id);
    const isNewOrganization = !selectedOrganization || newOrganization.id !== selectedOrganization.id;

    setSelectedOrganization(newOrganization);

    if (isNewOrganization) {
      setSelectedLocation(newOrganization?.location_set?.[0] || null);
    }
  };

  if (singleLevel === "installation")
    return (
      <MultiSelectInstallations
        className={className}
        disabled={!selectedLocation}
      />
    );

  if (singleLevel === "location")
    return (
      <SingleLevelLocationDropdown
        className={className}
        selectedOrganization={selectedOrganization}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
      />
    );

  if (singleLevel === "organization")
    return (
      <Dropdown
        className={className}
        options={organizations}
        placeholder="Select an organization"
        selectedItem={{
          name: selectedOrganization?.name,
          id: selectedOrganization?.id,
        }}
        onSelect={(option: any) => {
          setSelectedOrganization(organizations.find((o) => o.id === option.id));
          setSelectedLocation(null);
        }}
      />
    );

  return (
    <>
      <Dropdown
        options={organizations}
        placeholder="Select an organization"
        selectedItem={{
          name: selectedOrganization?.name,
          id: selectedOrganization?.id,
        }}
        onSelect={handleOrganizationSelect}
      />
      {selectedOrganization ? (
        <LocationDropdown
          selectedOrganization={selectedOrganization}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
        />
      ) : (
        <Dropdown
          options={[]}
          placeholder="Select a building"
          selectedItem={{ name: undefined, id: undefined }}
          onSelect={() => {}}
          disabled
        />
      )}
      <MultiSelectInstallations
        disabled={!selectedLocation}
      />
    </>
  );
}
